// AI-Summary-Panel — extrahiert aus InquiriesPage.js für bessere
// Modularisierung. Identische Funktionalität: triggert /ai-summary-Endpoint
// am Backend, zeigt headline, bullets, discrepancyWarning und openQuestions
// für die Inquiry-Detail-Ansicht.
import { useState } from 'react';
import { Sparkles, AlertCircle } from 'lucide-react';
import { authFetch } from '../../lib/auth-fetch';

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function AiSummaryPanel({ inquiry, onChange }) {
  const summary = inquiry?.aiSummaryDoc;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generate = async () => {
    setLoading(true); setError(null);
    try {
      const r = await authFetch(`/api/mail-inbound/inquiries/${inquiry.id}/ai-summary`, { method: 'POST' });
      if (!r.ok) throw new Error('KI-Zusammenfassung fehlgeschlagen');
      onChange?.();
    } catch (e) {
      setError(e.message || 'Fehler');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="ai-summary-panel">
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#9ec043]" /> KI-Zusammenfassung
        </h3>
        <button onClick={generate} disabled={loading}
                className="text-[11px] px-2 py-1 rounded bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 disabled:opacity-40 inline-flex items-center gap-1"
                data-testid="ai-summary-generate">
          {loading ? 'Generiert…' : (summary ? 'Neu generieren' : 'Generieren')}
        </button>
      </div>
      {error && <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mb-2">{error}</div>}
      {summary ? (
        <>
          <div className="text-[12px] font-semibold text-[#32373c] mb-1.5">{summary.headline}</div>
          {summary.discrepancyWarning && (
            <div className="mb-2 p-2 rounded-md border border-rose-200 bg-rose-50 text-[11px] text-rose-800 flex items-start gap-2"
                 data-testid="ai-summary-discrepancy">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <strong>Hinweis Unstimmigkeit:</strong> {summary.discrepancyWarning}
              </div>
            </div>
          )}
          <ul className="text-[12px] space-y-1 list-disc ml-4 text-gray-700">
            {(summary.bullets || []).map((b, i) => <li key={i}>{b}</li>)}
          </ul>
          {summary.openQuestions?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-1">Offene Punkte für Telefonat</div>
              <ul className="text-[12px] space-y-1 list-disc ml-4 text-amber-900">
                {summary.openQuestions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </div>
          )}
          <div className="text-[10px] text-gray-400 mt-2 flex items-center gap-2 flex-wrap">
            <span>Generiert {fmtDate(summary.generatedAt)}</span>
            {summary.bundledMailsCount > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                inkl. {summary.bundledMailsCount} gebündelte Folge-Mail{summary.bundledMailsCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="text-[11px] text-gray-400 italic">
          Noch keine Zusammenfassung — Klick auf „Generieren" wertet Mail-Text, Anhänge,
          strukturierte Daten und Notizen aus.
        </div>
      )}
    </div>
  );
}
